import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import {
  BaseSite,
  BaseSiteService,
  CmsService,
  Country,
  CurrencyService,
  InterceptorUtil,
  LanguageService,
  USE_CUSTOMER_SUPPORT_AGENT_TOKEN,
  User
} from '@spartacus/core';
import Cookies from 'js-cookie';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, tap, mergeMap, map } from 'rxjs/operators';

import { DpLanguageSelectorService } from '@app/core/services/language-selector.service';
import { DpUrlService } from '@app/core/services/url.service';
import { DpSiteContextCookiesService } from '@app-modules/site-context/services/site-context-cookies.service';
import { setCountryCode } from '@app-modules/site-context/store/actions';
import { countriesSelector } from '@app-modules/site-context/store/site-context.selectors';
import { DpGoogleTagManagerService } from '@app-shared/google-tag-manager';

@Injectable({ providedIn: 'root' })
export class DpMarketSelectorService {
  readonly #cookiesService = inject(DpSiteContextCookiesService);
  readonly #cmsService = inject(CmsService);
  readonly #languageSelector = inject(DpLanguageSelectorService);
  readonly #currencyService = inject(CurrencyService);

  constructor(
    private readonly httpClient: HttpClient,
    private readonly dpUrlService: DpUrlService,
    private readonly baseSiteService: BaseSiteService,
    private readonly gtmService: DpGoogleTagManagerService,
    private readonly languageService: LanguageService,
    private readonly siteContextStore: Store
  ) {}

  setMarketAndCountryCookie(marketCountry: Country | undefined): void {
    if (marketCountry) {
      this.#cookiesService.setCookie('market', marketCountry.market);
      this.#cookiesService.setCookie('country', marketCountry?.isocode ?? '');
      this.siteContextStore.dispatch(setCountryCode({ activeCountryCode: marketCountry.isocode ?? '' }));
    }
  }

  getUserAuthMarketAndCountry(): Observable<User> {
    return this.dpUrlService.urlParts$.pipe(
      mergeMap(({ market, language, currency }) => {
        const url = `${this.dpUrlService.API_ROUTE}/${market}/orgUsers/current`;
        const params = new HttpParams({
          fromObject: {
            lang: language,
            curr: currency
          }
        });
        let headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });

        // TODO: Use new HttpHeaders instead
        headers = InterceptorUtil.createHeader(USE_CUSTOMER_SUPPORT_AGENT_TOKEN, true, headers);
        return this.httpClient.get<User>(url, { params, headers });
      })
    );
  }

  setCurrent(
    marketIso: string,
    country: Country,
    requestedLang?: string,
    currency?: string
  ): Observable<BaseSite | undefined> {
    return this.baseSiteService.get(marketIso).pipe(
      distinctUntilChanged(),
      filter((baseSite): baseSite is BaseSite => !!baseSite),
      tap(({ baseStore }) => {
        const language = this.#languageSelector.getContextLanguage(requestedLang, baseStore);
        this.setMarketAndCountryCookie(country);
        this.#cookiesService.setCookie('language', language);
        this.#setContext(marketIso, language, currency);
      })
    );
  }

  setContextGeolocation(country: Country): Observable<BaseSite | undefined> {
    return this.baseSiteService.get(country?.market).pipe(
      filter((baseSite): baseSite is BaseSite => !!baseSite),
      tap(() => {
        this.setMarketAndCountryCookie(country);
        this.#setContext(country.market);
        this.#cmsService.refreshLatestPage();
      })
    );
  }

  setContextFromUrlOrCookie(
    urlMarket: string,
    urlLanguage: string,
    country?: Country
  ): Observable<BaseSite | undefined> {
    return this.siteContextStore.select(countriesSelector).pipe(
      map(
        (countries: Country[]) =>
          countries.find((item) => item.isocode === urlMarket.toUpperCase()) ||
          countries.find((item) => item.isocode === Cookies.get('country')) ||
          countries.find((item) => item.isocode === 'UK')
      ),
      filter(this.#isDefined),
      switchMap((matchingCountry: Country) => {
        country = matchingCountry;
        return this.baseSiteService.get(country.market);
      }),
      filter((baseSite): baseSite is BaseSite => !!baseSite),
      tap(({ baseStore }) => {
        const language = this.#languageSelector.getContextLanguage(urlLanguage, baseStore);
        this.siteContextStore.dispatch(setCountryCode({ activeCountryCode: country?.isocode ?? '' }));
        this.#cookiesService.setCookie('language', language);
        this.setMarketAndCountryCookie(country);
        this.#setContext(country?.market || 'uk-ie', language);
      })
    );
  }

  setActiveCountry(selected: Country): Observable<BaseSite | undefined> {
    this.#pushGTMTag(selected);
    const marketCode = selected.market;
    const activeLang = Cookies.get('language');
    return this.setCurrent(marketCode, selected, activeLang);
  }

  readonly #isDefined = <T>(value: T | undefined): value is T => !!value;

  #setContext(baseSiteIso?: string, languageIso?: string, currencyIso?: string): void {
    if (baseSiteIso) {
      this.baseSiteService.setActive(baseSiteIso);
    }
    if (languageIso) {
      this.languageService.setActive(languageIso);
    }
    if (currencyIso) {
      this.#currencyService.setActive(currencyIso);
    }
  }

  #pushGTMTag(country: Country): void {
    void this.gtmService.pushTag({
      event: 'countrySelector',
      countrySelect: country.name
    });
  }
}
